import { type Ref } from 'vue'
import type { MultiselectOption } from '@ironsight/types'
import {
  searchTextsMatch,
  type SearchSelectAllMode,
  useSelectAllSentinel,
} from '@/components/jobs/job-search-page/useSelectAllSentinel'

type UseMultiselectSelectAllArgs = {
  value: () => unknown
  searchText: Ref<string>
  checkedBoxes: Ref<Array<string | number>>
  filteredOptions: () => MultiselectOption[]
  resolveOption: (value: string | number) => MultiselectOption
  emitInput: (value: MultiselectOption[]) => void
  searchSelectAllMode: SearchSelectAllMode
}

export { searchTextsMatch }
export type { SearchSelectAllMode }

export function optionMatchesMultiselectSearchText(
  option: Pick<MultiselectOption, 'label' | 'description'>,
  searchText: string,
): boolean {
  const normalizedSearchText = searchText.toLowerCase()
  return (
    option.label.toLowerCase().includes(normalizedSearchText) ||
    (option.description?.toLowerCase().includes(normalizedSearchText) ?? false)
  )
}

export function useMultiselectSelectAll({
  value,
  searchText,
  checkedBoxes,
  filteredOptions,
  resolveOption,
  emitInput,
  searchSelectAllMode,
}: UseMultiselectSelectAllArgs) {
  const sentinel = useSelectAllSentinel<MultiselectOption, string | number>({
    value,
    searchText,
    selectedValues: checkedBoxes,
    visibleItems: filteredOptions,
    getItemValue: (item) => item.value,
    itemToOption: (item) => item,
    resolveOption,
    normalValueToKey: (item) => {
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return (item as MultiselectOption).value
      }
      if (typeof item === 'string' || typeof item === 'number') {
        return item
      }
      return undefined
    },
    emitSelectAllValue: emitInput,
    emitNormalValue: (selectedValues) => emitInput(selectedValues.map(resolveOption)),
    itemMatchesSearchText: optionMatchesMultiselectSearchText,
    searchSelectAllMode,
  })

  return {
    isSelectAllMode: sentinel.isSelectAllMode,
    activeSelectAllSearchText: sentinel.activeSelectAllSearchText,
    excludedValues: sentinel.excludedValues,
    includedValues: sentinel.includedValues,
    excludedSearchTexts: sentinel.excludedSearchTexts,
    optionMatchesSearchText: optionMatchesMultiselectSearchText,
    optionMatchesSelectAllSearchScope: sentinel.itemMatchesSelectAllScope,
    optionMatchesExcludedSearchScope: sentinel.itemMatchesExcludedScope,
    searchTextsMatch,
    isItemChecked: sentinel.isItemSelected,
    includeItem: sentinel.includeItem,
    toggleItem: sentinel.toggleItem,
    selectAllFiltered: sentinel.selectAllFiltered,
    deselectAllFiltered: sentinel.deselectAllFiltered,
    areAllFilteredSelected: sentinel.areAllFilteredSelected,
    getOptionsFromCheckedBoxes: sentinel.getOptionsFromSelectedValues,
    mapCheckedBoxValues: sentinel.mapSelectedValues,
    checkedBoxValuesChanged: sentinel.selectedValuesChanged,
  }
}
